import { HissaReportType } from "../../../types/common";

export type ReportColumn = {
  key: string;
  label: string;
};

const vestingTrancheColumns = Array.from({ length: 30 }).flatMap((_, idx) => {
  const index = idx + 1;
  return [
    { key: `vesting_type_${index}`, label: `Vesting Type (${index})` },
    {
      key: `tranch_${index}_vesting_state`,
      label: `${index} Tranche Vesting State`,
    },
    { key: `tranch_${index}_date`, label: `${index} Tranche Date` },
    {
      key: `tranch_${index}_total_options`,
      label: `${index} Tranche Total Options`,
    },
    {
      key: `tranch_${index}_vested_options`,
      label: `${index} Tranche Vested Options`,
    },
    {
      key: `tranch_${index}_lapsed_options`,
      label: `${index} Tranche Lapsed Options`,
    },
  ];
});

const nomineeColumns = Array.from({ length: 30 }).flatMap((_, idx) => {
  const index = idx + 1;
  return [
    { key: `nominee_${index}_name`, label: `Nominee ${index} Name` },
    {
      key: `nominee_${index}_relation`,
      label: `Nominee ${index} Relation`,
    },
    {
      key: `nominee_${index}_percentage`,
      label: `Nominee ${index} Percentage`,
    },
  ];
});

const financialGrantSheetColumns: ReportColumn[] = [
  { key: "employee_id", label: "Employee ID" },
  { key: "employee_name", label: "Employee Name" },
  { key: "doj", label: "Date of Joining" },
  { key: "employment_status", label: "Employment Status" },
  { key: "resignation_date", label: "Date of Resignation" },
  { key: "grant_id", label: "Grant Identifier" },
  { key: "grant_type", label: "Grant Type" },
  { key: "grant_date", label: "Date of Grant" },
  { key: "granted_options", label: "Granted Options" },
  { key: "grant_price", label: "Grant Price" },
  { key: "forfeited", label: "Forfeited" },
  { key: "cashed_out", label: "Cashed Out" },
  { key: "sold_shares", label: "Sold Options" },
  { key: "active_shares", label: "Active Options" },
  { key: "active_vested", label: "Active Vested" },
  { key: "active_unvested", label: "Active Unvested" },
  { key: "lapsed", label: "Lapsed" },
  { key: "vesting_schedule", label: "Vesting Schedule" },
  { key: "vesting_type", label: "Vesting Type" },
  { key: "financial_year", label: "Financial Year" },
];

const financialVestingSheetColumns: ReportColumn[] = [
  { key: "employee_id", label: "Employee ID" },
  { key: "employee_name", label: "Employee Name" },
  { key: "employment_status", label: "Employment Status" },
  { key: "doj", label: "Date of Joining" },
  { key: "dol", label: "Date of Leaving" },
  { key: "grant_identifier", label: "Grant Identifier" },
  { key: "grant_type", label: "Grant Type" },
  { key: "grant_status", label: "Grant Status" },
  { key: "plan_name", label: "Plan Name" },
  { key: "plan_type", label: "Plan Type" },
  { key: "date_of_grant", label: "Date of Grant" },
  { key: "options_granted", label: "Options Granted (Total)" },
  { key: "grant_price", label: "Grant Price" },
  { key: "value_at_grant", label: "Value at time of Grant (per option)" },
  { key: "current_value", label: "Current Value (per option)" },
  { key: "cashed_out", label: "Options Cashed out (Total)" },
  { key: "stock_exercised", label: "Options Stock Exercised (Total)" },
  { key: "forfeited", label: "Options Forfeited (Total)" },
  { key: "projected_vest_date", label: "Projected Vesting Date" },
  { key: "projected_vested", label: "Projected Vested Options" },
  { key: "actual_vested", label: "Actual Vested Options" },
  { key: "lapsed", label: "Lapsed Options" },
  { key: "trigger_type", label: "Trigger Type" },
  { key: "status", label: "Status" },
  {
    key: "value_at_grant_projected",
    label: "Value at time of Grant (For Projected Vested Options)",
  },
  {
    key: "current_value_projected",
    label: "Current Value (For Projected Vested Options)",
  },
  { key: "net_vested", label: "Net Vested" },
  { key: "grant_fy", label: "Grant Financial Year" },
  { key: "vesting_fy", label: "Vesting Financial Year" },
];

const reportColumnsByType: Partial<Record<HissaReportType, ReportColumn[]>> = {
  [HissaReportType.GRANT_REPORT]: [
    { key: "grant_identifier", label: "Grant Identifier" },
    { key: "grant_type", label: "Grant Type" },
    { key: "employee_name", label: "Employee Name" },
    { key: "employee_identifier", label: "Employee Identifier" },
    { key: "employee_status", label: "Employee Status" },
    { key: "plan_name", label: "Plan Name" },
    { key: "grant_status", label: "Grant Status" },
    { key: "granted_options", label: "Granted Options" },
    { key: "grant_price", label: "Grant Price" },
    { key: "grant_price_type", label: "Grant Price Type" },
    { key: "grant_date", label: "Grant Date" },
    { key: "vested", label: "Vested" },
    { key: "stock_exercised_options", label: "Stock Exercised (Options)" },
    { key: "cashed_out", label: "Cashed Out" },
    { key: "lapsed", label: "Lapsed" },
    { key: "forfeited", label: "Forfeited" },
    { key: "unvested", label: "Unvested" },
    { key: "net_vested", label: "Net Vested" },
    { key: "outstanding_options", label: "Outstanding Options" },
    { key: "sold_shares", label: "Sold Shares" },
    { key: "value_at_time_of_grant", label: "Value at Time of Grant" },
    { key: "price_per_grant_source", label: "Price Per Grant Source" },
    { key: "current_valuation", label: "Current Valuation" },
    { key: "grant_fy", label: "Grant FY" },
  ],
  [HissaReportType.VEST_WISE_REPORT]: [
    { key: "grant_id", label: "Grant ID" },
    { key: "employee_name", label: "Employee Name" },
    { key: "emp_id", label: "Employee ID" },
    { key: "employee_status", label: "Employee Status" },
    { key: "date_of_joining", label: "Date of Joining" },
    { key: "date_of_leaving", label: "Date of Leaving" },
    { key: "grant_date", label: "Grant Date" },
    { key: "grant_status", label: "Grant Status" },
    { key: "plan_name", label: "Plan Name" },
    { key: "options_granted", label: "Options Granted" },
    ...vestingTrancheColumns,
  ],
  [HissaReportType.CANCELLATION_REPORT]: [
    { key: "plan_name", label: "Plan Name" },
    { key: "employee_name", label: "Employee Name" },
    { key: "employee_identifier", label: "Employee Identifier" },
    { key: "employee_status", label: "Employee Status" },
    { key: "last_working_date", label: "Last Working Date" },
    { key: "reason_for_resignation", label: "Reason for Resignation" },
    { key: "grant_id", label: "Grant ID" },
    { key: "grant_date", label: "Grant Date" },
    { key: "date_of_vesting", label: "Date of Vesting" },
    { key: "cancellation_date", label: "Cancellation Date" },
    { key: "reason_for_cancellation", label: "Reason for Cancellation" },
    { key: "cashed_out", label: "Cashed Out" },
    { key: "forfeited", label: "Forfeited" },
    { key: "lapsed", label: "Lapsed" },
    { key: "total_cancelled", label: "Total Cancelled" },
    { key: "grant_fy", label: "Grant FY" },
  ],
  [HissaReportType.EMPLOYEE_WISE_REPORT]: [
    { key: "identifier", label: "Identifier" },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "status", label: "Status" },
    { key: "subsidiary_company", label: "Subsidiary Company" },
    { key: "date_of_joining", label: "Date of Joining" },
    { key: "last_day", label: "Last Day" },
    { key: "resigned_date", label: "Resigned Date" },
    { key: "designation", label: "Designation" },
    { key: "department", label: "Department" },
    { key: "credentials_generated", label: "Credentials Generated" },
    { key: "total_units_granted", label: "Total Units Granted" },
    { key: "units_vested", label: "Units Vested" },
    { key: "stock_exercised", label: "Stock Exercised" },
    { key: "cashed_out", label: "Cashed Out" },
    { key: "stock_exercised_requested", label: "Stock Exercised Requested" },
    { key: "cash_exercise_requested", label: "Cash Exercise Requested" },
    { key: "lapsed", label: "Lapsed" },
    { key: "forefited", label: "Forfeited" },
    { key: "sold_options", label: "Sold Options" },
    { key: "sold_shares", label: "Sold Shares" },
    { key: "notes", label: "Notes" },
    ...nomineeColumns,
  ],
  [HissaReportType.EXERCISE_REPORT]: [
    { key: "employee_id", label: "Employee ID" },
    { key: "employee_name", label: "Employee Name" },
    { key: "employee_status", label: "Employee Status" },
    { key: "last_working_date", label: "Last Working Date" },
    { key: "grant_id", label: "Grant ID" },
    { key: "grant_date", label: "Grant Date" },
    { key: "vested_options", label: "Vested Options" },
    { key: "vesting_period_complete", label: "No of Vesting Period Complete" },
    { key: "plan_name", label: "Plan Name" },
    { key: "last_vesting_date", label: "Last Vesting Date" },
    { key: "transaction_type", label: "Transaction Type" },
    { key: "request_raise_date", label: "Request Raise Date" },
    { key: "options_exercised", label: "Options Exercised" },
    { key: "exercise_price", label: "Exercise Price" },
    { key: "exercise_price_type", label: "Exercise Price Type" },
    {
      key: "exercise_amount_employee_payable",
      label: "Exercise Amount (Employee Payable)",
    },
    { key: "exercise_date", label: "Exercise Date" },
    { key: "latest_conversion_ratio", label: "Latest Conversion Ratio" },
    { key: "latest_conversion_method", label: "Latest Conversion Method" },
    {
      key: "latest_shares_with_conversion_method",
      label: "Latest Shares To Be Issued (With Conversion Method)",
    },
    {
      key: "latest_shares_without_conversion_method",
      label: "Latest Shares To Be Issued (With No Conversion Method)",
    },
    {
      key: "conversion_ratio_as_of_report_date",
      label: "Conversion Ratio As of Report Date",
    },
    {
      key: "conversion_method_as_of_report_date",
      label: "Conversion Method As of Report Date",
    },
    {
      key: "shares_as_of_report_date_with_conversion_method",
      label: "Shares To Be Issued As of Report Date (With Conversion Method)",
    },
    {
      key: "shares_as_of_report_date_without_conversion_method",
      label:
        "Shares To Be Issued As of Report Date (With No Conversion Method)",
    },
  ],
  [HissaReportType.CASHOUT_REPORT]: [
    { key: "employee_id", label: "Employee ID" },
    { key: "employee_name", label: "Employee Name" },
    { key: "employee_status", label: "Employee Status" },
    { key: "last_working_date", label: "Last Working Date" },
    { key: "grant_id", label: "Grant ID" },
    { key: "grant_date", label: "Grant Date" },
    { key: "vested_options", label: "Vested Options" },
    { key: "vesting_period_complete", label: "No of Vesting Period Complete" },
    { key: "plan_name", label: "Plan Name" },
    { key: "last_vesting_date", label: "Last Vesting Date" },
    { key: "transaction_type", label: "Transaction Type" },
    { key: "request_raise_date", label: "Request Raise Date" },
    { key: "options_cashed_out", label: "Options Cashed Out" },
    { key: "exercise_price", label: "Exercise Price" },
    { key: "exercise_price_type", label: "Exercise Price Type" },
    {
      key: "exercise_amount_employee_payable",
      label: "Exercise Amount (Employee Payable)",
    },
    { key: "total_amount", label: "Total Amount" },
    { key: "perquisite_value", label: "Perquisite Value" },
    { key: "cashout_date", label: "Cashout Date" },
    { key: "conversion_method", label: "Conversion Method" },
  ],
  [HissaReportType.LIQUIDITY_REPORT]: [
    { key: "employee_id", label: "Employee ID" },
    { key: "employee_name", label: "Employee Name" },
    { key: "employee_status", label: "Employee Status" },
    { key: "event_name", label: "Event Name" },
    { key: "granted_options", label: "Granted Options" },
    { key: "vested_options", label: "Vested Options" },
    { key: "exercised_options", label: "Exercised Options" },
    { key: "eligibility", label: "Eligibility" },
    { key: "willingness", label: "Willingness" },
    { key: "status", label: "Status" },
    { key: "total_amount_payable", label: "Total Amount Payable" },
    { key: "total_cashout_amount", label: "Total Cashout Amount" },
    { key: "net_payout_amount", label: "Net Payout Amount" },
  ],
  [HissaReportType.BUYBACK_REPORT]: [
    { key: "employee_id", label: "Employee ID" },
    { key: "employee_name", label: "Employee Name" },
    { key: "employee_status", label: "Employee Status" },
    { key: "event_name", label: "Event Name" },
    { key: "granted_options", label: "Granted Options" },
    { key: "vested_options", label: "Vested Options" },
    { key: "exercised_options", label: "Exercised Options" },
    { key: "eligibility", label: "Eligibility" },
    { key: "willingness", label: "Willingness" },
    { key: "status", label: "Status" },
    { key: "total_amount_payable", label: "Total Amount Payable" },
    { key: "total_cashout_amount", label: "Total Cashout Amount" },
    { key: "net_payout_amount", label: "Net Payout Amount" },
  ],
  [HissaReportType.ACCEPTANCE_REPORT]: [
    { key: "employee_identifier", label: "Employee Identifier" },
    { key: "employee_name", label: "Employee Name" },
    { key: "email_id", label: "Email ID" },
    { key: "employee_status", label: "Employee Status" },
    { key: "resignation_date", label: "Resignation Date" },
    { key: "plan_name", label: "Plan Name" },
    { key: "grant_id", label: "Grant ID" },
    { key: "grant_date", label: "Grant Date" },
    { key: "options_granted", label: "Options Granted" },
    { key: "grant_status", label: "Grant Status" },
    { key: "accept_by", label: "Accept By" },
    { key: "accepted_date", label: "Accepted Date" },
    { key: "creator", label: "Creator" },
    { key: "created_date", label: "Created Date" },
    { key: "approved_by", label: "Approved By" },
    { key: "approved_date", label: "Approved Date" },
    { key: "offered_date", label: "Offered Date" },
  ],
  [HissaReportType.FINANCIAL_REPORT]: [
    { key: "employee_id", label: "Employee ID" },
    { key: "employee_name", label: "Employee Name" },
    { key: "doj", label: "Date of Joining" },
    { key: "employment_status", label: "Employment Status" },
    { key: "resignation_date", label: "Date of Resignation" },
    { key: "grant_id", label: "Grant Identifier" },
    { key: "grant_type", label: "Grant Type" },
    { key: "grant_date", label: "Date of Grant" },
    { key: "granted_options", label: "Granted Options" },
    { key: "grant_price", label: "Grant Price" },
    { key: "forfeited", label: "Forfeited" },
    { key: "cashed_out", label: "Cashed Out" },
    { key: "sold_shares", label: "Sold Options" },
    { key: "active_shares", label: "Active Options" },
    { key: "active_vested", label: "Active Vested" },
    { key: "active_unvested", label: "Active Unvested" },
    { key: "lapsed", label: "Lapsed" },
    { key: "vesting_schedule", label: "Vesting Schedule" },
    { key: "vesting_type", label: "Vesting Type" },
    { key: "financial_year", label: "Financial Year" },
    { key: "dol", label: "Date of Leaving" },
    { key: "grant_identifier", label: "Grant Identifier (Vesting Sheet)" },
    { key: "grant_status", label: "Grant Status" },
    { key: "plan_name", label: "Plan Name" },
    { key: "plan_type", label: "Plan Type" },
    { key: "options_granted", label: "Options Granted (Total)" },
    {
      key: "value_at_grant",
      label: "Value at Time of Grant (Per Option)",
    },
    { key: "current_value", label: "Current Value (Per Option)" },
    { key: "stock_exercised", label: "Options Stock Exercised (Total)" },
    { key: "projected_vest_date", label: "Projected Vesting Date" },
    { key: "projected_vested", label: "Projected Vested Options" },
    { key: "actual_vested", label: "Actual Vested Options" },
    { key: "trigger_type", label: "Trigger Type" },
    { key: "status", label: "Status" },
    {
      key: "value_at_grant_projected",
      label: "Value at Time of Grant (Projected)",
    },
    {
      key: "current_value_projected",
      label: "Current Value (Projected)",
    },
    { key: "net_vested", label: "Net Vested" },
    { key: "grant_fy", label: "Grant Financial Year" },
    { key: "vesting_fy", label: "Vesting Financial Year" },
  ],
  [HissaReportType.SH6_REPORT]: [
    { key: "s_no", label: "S.No" },
    { key: "name_of_the_grantee", label: "Name of the Grantee" },
    {
      key: "number_of_options_granted",
      label: "Number of Options Granted",
    },
    { key: "total_options_vested", label: "Total Options Vested" },
    { key: "options_vested_breakup", label: "Options Vested Breakup" },
    {
      key: "date_on_which_options_vested",
      label: "Date on Which Options Vested",
    },
    { key: "total_options_exercised", label: "Total Options Exercised" },
    {
      key: "options_exercised_breakup",
      label: "Options Exercised Breakup",
    },
    {
      key: "date_on_which_options_exercised",
      label: "Date on Which Options Exercised",
    },
    { key: "exercised_period", label: "Exercised Period" },
    { key: "exercise_price_inr", label: "Exercise Price (INR)" },
    {
      key: "number_of_shares_arising_as_result_of_exercise_of_option",
      label: "Number of Shares Arising as Result of Exercise of Option",
    },
    {
      key: "folio_no_of_register_of_members_having_respective_entry",
      label: "Folio No. of Register of Members",
    },
    { key: "lock_in_period_if_any", label: "Lock in Period if Any" },
    {
      key: "options_lapsed_due_to_vested_not_exercised",
      label: "Options Lapsed (Vested Not Exercised)",
    },
    {
      key: "options_lapsed_due_to_unvested",
      label: "Options Lapsed (Unvested)",
    },
    {
      key: "options_cancelled_cash_outs",
      label: "Options Cancelled (Cash Outs)",
    },
    {
      key: "total_number_of_options_in_force",
      label: "Total Number of Options in Force",
    },
    {
      key: "amount_forfeited_refunded_if_option_is_not_exercised",
      label: "Amount Forfeited/Refunded if Option Is Not Exercised",
    },
    {
      key: "any_variation_of_terms_of_the_scheme_and_its_effects",
      label: "Variation of Terms of Scheme and Effects",
    },
    { key: "signature", label: "Signature" },
    { key: "remarks", label: "Remarks" },
  ],
  [HissaReportType.SECTION_10_C_REPORT]: [
    { key: "sr_no", label: "Sr No" },
    { key: "employee_id", label: "Employee ID" },
    { key: "name_opt", label: "Name (Opt)" },
    { key: "no_of_shares", label: "No of Shares" },
    { key: "esop_scheme_name", label: "ESOP Scheme Name" },
    { key: "type", label: "Type" },
    { key: "date", label: "Date" },
    { key: "isin", label: "ISIN" },
  ],
};

const unsupportedTypes = new Set<HissaReportType>([
  HissaReportType.POOL_STATEMENT,
  HissaReportType.ESOP_ACCOUNTING,
  HissaReportType.SEBI_REPORT,
]);

export const isColumnChooserSupported = (
  reportType?: HissaReportType
): boolean => {
  if (!reportType) return false;
  return !unsupportedTypes.has(reportType);
};

export const getReportColumns = (
  reportType?: HissaReportType
): ReportColumn[] => {
  if (!reportType) return [];
  return reportColumnsByType[reportType] || [];
};

export const getFinancialReportSheetColumns = () => ({
  grantSheet: financialGrantSheetColumns,
  vestingSheet: financialVestingSheetColumns,
});
